/**
 * Payroll Normalizers (PAYROLL)
 *
 * DRR-027: Normalizers for payroll resources from the PAYROLL subgraph
 *
 * STUB: Expand when Payroll types are available in generated/graphql.ts
 *
 * These normalizers will convert Payroll resources (PayrollRun, Employee)
 * into NormalizedEntity<T> with SOX-compliant safety levels.
 */

import { SafetyLevel } from '@/types/graphql-compat';

import type { ComplianceFramework, NormalizedEntity } from '@/types/normalized-entity';

import { UnifiedStatus } from '@/types/normalized-entity';

// ============================================================================
// PLACEHOLDER TYPES
// These will be replaced by imports from @/generated/graphql when available
// ============================================================================

/**
 * Payroll run status
 */
export type PayrollRunStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'PROCESSING'
  | 'PROCESSED'
  | 'FAILED'
  | 'CANCELLED';

/**
 * Placeholder PayrollRun type - will be replaced by PAYROLL subgraph type
 */
export interface PayrollRun {
  __typename: 'PayrollRun';
  id: string;
  tenantId: string;
  name?: string;
  periodStart: string;
  periodEnd: string;
  status: PayrollRunStatus;
  totalGross?: number;
  totalNet?: number;
  totalDeductions?: number;
  employeeCount?: number;
  approvedBy?: string;
  approvedAt?: string;
  processedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Employee status
 */
export type EmployeeStatus = 'ACTIVE' | 'INACTIVE' | 'TERMINATED' | 'ON_LEAVE';

/**
 * Placeholder Employee type - will be replaced by PAYROLL subgraph type
 */
export interface Employee {
  __typename: 'Employee';
  id: string;
  tenantId: string;
  employeeNumber?: string;
  firstName: string;
  lastName: string;
  email: string;
  department?: string;
  position?: string;
  status: EmployeeStatus;
  hireDate: string;
  terminationDate?: string;
  salary?: number;
  payFrequency?: 'WEEKLY' | 'BIWEEKLY' | 'SEMIMONTHLY' | 'MONTHLY';
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// STATUS MAPPINGS
// ============================================================================

/**
 * Map PayrollRun status to UnifiedStatus
 */
const PAYROLL_RUN_STATUS_MAP: Record<PayrollRunStatus, UnifiedStatus> = {
  DRAFT: UnifiedStatus.PENDING,
  PENDING_APPROVAL: UnifiedStatus.BLOCKED,
  APPROVED: UnifiedStatus.PENDING,
  PROCESSING: UnifiedStatus.ACTIVE,
  PROCESSED: UnifiedStatus.COMPLETED,
  FAILED: UnifiedStatus.FAILED,
  CANCELLED: UnifiedStatus.CANCELLED,
} as const;

/**
 * Map Employee status to UnifiedStatus
 */
const EMPLOYEE_STATUS_MAP: Record<EmployeeStatus, UnifiedStatus> = {
  ACTIVE: UnifiedStatus.ACTIVE,
  INACTIVE: UnifiedStatus.BLOCKED,
  TERMINATED: UnifiedStatus.CANCELLED,
  ON_LEAVE: UnifiedStatus.BLOCKED,
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get display name for a PayrollRun
 */
function getPayrollRunDisplayName(payrollRun: PayrollRun): string {
  if (payrollRun.name) {
    return payrollRun.name;
  }

  // Format: "Payroll 2024-01-01 to 2024-01-15"
  const start = payrollRun.periodStart.split('T')[0];
  const end = payrollRun.periodEnd.split('T')[0];
  return `Payroll ${start} to ${end}`;
}

/**
 * Get display name for an Employee
 */
function getEmployeeDisplayName(employee: Employee): string {
  const fullName = `${employee.firstName} ${employee.lastName}`;
  if (employee.employeeNumber) {
    return `${fullName} (${employee.employeeNumber})`;
  }
  return fullName;
}

/**
 * Determine safety level based on payroll amount
 */
function getPayrollRunSafetyLevel(payrollRun: PayrollRun): SafetyLevel {
  const totalGross = payrollRun.totalGross ?? 0;

  // Large payrolls are critical (over $100k)
  if (totalGross > 100000) {
    return SafetyLevel.CRITICAL;
  }
  // Medium payrolls are high priority (over $10k)
  if (totalGross > 10000) {
    return SafetyLevel.HIGH;
  }
  // Small payrolls are medium priority
  return SafetyLevel.MEDIUM;
}

/**
 * Determine if payroll run requires dual approval
 */
function requiresDualApprovalForPayroll(payrollRun: PayrollRun): boolean {
  const totalGross = payrollRun.totalGross ?? 0;
  // Payrolls over $50k require dual approval for SOX compliance
  return totalGross > 50000;
}

// ============================================================================
// PAYROLL RUN NORMALIZER
// ============================================================================

/**
 * Normalize a PayrollRun into NormalizedEntity<PayrollRun>
 *
 * Payroll runs involve financial transactions and require SOX compliance.
 *
 * @param payrollRun - The PayrollRun from PAYROLL
 * @returns Fully typed NormalizedEntity with PayrollRun preserved in domainData
 */
export function normalizePayrollRun(payrollRun: PayrollRun): NormalizedEntity<PayrollRun> {
  const safetyLevel = getPayrollRunSafetyLevel(payrollRun);

  // Map status to unified status
  const unifiedStatus = PAYROLL_RUN_STATUS_MAP[payrollRun.status] ?? UnifiedStatus.PENDING;

  // Payroll is blocked if pending approval or cancelled
  const isBlockedBySafety =
    payrollRun.status === 'PENDING_APPROVAL' ||
    payrollRun.status === 'CANCELLED' || // STRING-ENUM-EXCEPTION: no generated enum for this subgraph status yet
    payrollRun.status === 'FAILED';

  return {
    id: payrollRun.id,
    displayName: getPayrollRunDisplayName(payrollRun),
    sourceSubgraph: 'payroll',
    sourceType: 'PayrollRun',
    safetyLevel,
    requiresDualApproval: requiresDualApprovalForPayroll(payrollRun),
    complianceFrameworks: ['SOX'] as ComplianceFramework[],
    unifiedStatus,
    isBlockedBySafety,
    domainData: payrollRun,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// EMPLOYEE NORMALIZER
// ============================================================================

/**
 * Normalize an Employee into NormalizedEntity<Employee>
 *
 * Employee data contains PII and requires appropriate protection.
 *
 * @param employee - The Employee from PAYROLL
 * @returns Fully typed NormalizedEntity with Employee preserved in domainData
 */
export function normalizeEmployee(employee: Employee): NormalizedEntity<Employee> {
  // Employee data is medium priority (PII but not financial)
  const safetyLevel = SafetyLevel.MEDIUM;

  // Map status to unified status
  const unifiedStatus = EMPLOYEE_STATUS_MAP[employee.status] ?? UnifiedStatus.PENDING;

  // Employee is blocked if inactive, terminated, or on leave
  const isBlockedBySafety =
    employee.status === 'INACTIVE' ||
    employee.status === 'TERMINATED' ||
    employee.status === 'ON_LEAVE';

  return {
    id: employee.id,
    displayName: getEmployeeDisplayName(employee),
    sourceSubgraph: 'payroll',
    sourceType: 'Employee',
    safetyLevel,
    requiresDualApproval: false, // Employee records don't require dual approval
    complianceFrameworks: ['SOX'] as ComplianceFramework[],
    unifiedStatus,
    isBlockedBySafety,
    domainData: employee,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Check if an object is a PayrollRun
 */
export function isPayrollRun(obj: unknown): obj is PayrollRun {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'PayrollRun'
  );
}

/**
 * Check if an object is an Employee
 */
export function isEmployee(obj: unknown): obj is Employee {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Employee'
  );
}
